// FlowsConditions.jsx — CONDITIONS AT HOME strip (Bay Flows Phase 3, T16).
//
// Glanceable night-out context: six stats on one quiet solid-glass shelf
// directly under the HomeChip (design addendum §1). Renders ONLY in home
// state 'set' (binding ruling 3) — FlowsApp gates the mount; this component
// owns its data via lib/fetch-conditions.js (Worker-first → direct Open-Meteo
// fallback, §6 contract) keyed on the home station id, so a home change
// refetches and a toggle-back serves the §3.5 in-memory session cache.
//
// THE NAMED RISK (§2, retired by construction): hue lives on a non-text 8px
// dot ringed in the theme muted token; meaning lives in ink number + an
// always-present category word. Zero colored text, zero colored wash under
// text. Surface is glassStyle(darkMode,'solid') — no backdrop-filter, so the
// composite Jace measures against (#141414 dark / #FEFDFC light) is
// deterministic. Do not swap 'solid' for 'panel'/'card'.
//
// DEGRADE (§3.3): missing field → that cell absent (never a "0" or "—");
// both fetch legs fail → the strip is absent entirely; no aria-live (§4).
// Nothing here is focusable — the strip adds zero tab stops (AC-P3-3).

const { useState, useEffect } = React;

// Lens palette — spec §0.5, defined once per file, never re-derived.
// The strip only needs the meaning-carrier tokens: ink (values), muted
// (meta words + the dot RING — the measured ≥3:1 boundary), hairline.
const COND_LENS = {
  dark: { ink: '#E9E7E2', muted: '#94897B', hairline: 'rgba(255,255,255,0.08)' },
  light: { ink: '#191512', muted: '#6B5B4F', hairline: 'rgba(26,23,20,0.12)' },
};

// Copy — addendum §4 + controller ruling (progress.md §PHASE 3 kickoff,
// T16 brief): the visible heading is the spec's own literal "CONDITIONS AT
// HOME" — NOT the addendum's SKY CHECK default. Category words live in the
// lib's tables (single source with the band ladder).
const COND_COPY = {
  KICKER: 'CONDITIONS AT HOME',              // controller-ratified heading
  ARIA_NAME: 'Conditions at ',               // W1a prefix — + {station}
  // R1 (LICENSE-REQUIRED): Open-Meteo weather/AQI ships under CC-BY 4.0, which
  // requires a visible link next to the displayed data. All-caps house style;
  // aria-label spells it for AT.
  ATTR_TEXT: 'WEATHER BY OPEN-METEO.COM',
  ATTR_HREF: 'https://open-meteo.com/',
  ATTR_ARIA: 'Weather data by Open-Meteo',
};

// Fixed skeleton/cell dimensions (§3.1 — CLS-critical): the skeleton must
// reserve EXACTLY the resolved height. Value line = font-size × lineHeight 1;
// meta line = 9px × 1.1 = 9.9px; cell = value + gap 2 + meta + padding 4.
const CELL_METRICS = {
  desktop: { valuePx: 18, metaPx: 9.9, minHeight: 34 },
  mobile: { valuePx: 16, metaPx: 9.9, minHeight: 32 },
};

// sr-only — same clip pattern as FlowsApp's live region.
const COND_SR_ONLY = {
  position: 'absolute', width: 1, height: 1, margin: -1, padding: 0,
  overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
};

// Build the visible stat list from a normalized reading (§1.3 order —
// TEMP·WIND·HUMIDITY row1, SUNSET·UV·AQI row2 on the 3×2 wrap). A null field
// contributes NO entry (§3.3 per-stat hide). sr strings are W5/W6 verbatim.
function buildConditionStats(data, lib) {
  const stats = [];
  if (data.tempF !== null && data.tempF !== undefined) {
    const t = Math.round(data.tempF);
    stats.push({ key: 'temp', value: `${t}°`, meta: 'TEMP', sr: `Temperature ${t} degrees Fahrenheit` });
  }
  if (data.windMph !== null && data.windMph !== undefined) {
    const w = Math.round(data.windMph);
    stats.push({ key: 'wind', value: String(w), meta: 'MPH · WIND', sr: `Wind ${w} miles per hour` });
  }
  if (data.humidity !== null && data.humidity !== undefined) {
    const h = Math.round(data.humidity);
    stats.push({ key: 'humidity', value: `${h}%`, meta: 'HUMIDITY', sr: `Humidity ${h} percent` });
  }
  if (data.sunset && window.formatClock12) {
    // §1.4 — the existing §3.6 Pacific clock formatter, compact chip dialect
    // ("8:31P"), the same clock language the last-train chips speak.
    const compact = window.formatClock12(data.sunset, { compact: true });
    const full = window.formatClock12(data.sunset);
    if (compact) stats.push({ key: 'sunset', value: compact, meta: 'SUNSET', sr: `Sunset ${full}`, moon: true });
  }
  const uv = lib.resolveUV(data.uvNow, data.uvMax);
  if (uv) {
    stats.push({
      key: 'uv',
      value: uv.display,
      meta: uv.night ? `UV · PEAK ${uv.peak}` : `UV · ${uv.category}`,
      sr: uv.night ? `UV index 0 now, peaked at ${uv.peak} today` : `UV index ${uv.display}, ${uv.category}`,
      band: uv.band,
    });
  }
  if (data.aqi !== null && data.aqi !== undefined) {
    const a = Math.round(data.aqi);
    const band = lib.aqiBand(a);
    const cat = lib.AQI_CATEGORIES[band];
    stats.push({ key: 'aqi', value: String(a), meta: `AQI · ${cat}`, sr: `Air quality index ${a}, ${cat}`, band });
  }
  return stats;
}

function FlowsConditions({ station, darkMode, radius, isMobile, reducedMotion }) {
  const lib = window.FlowsConditionsLib;
  const lens = darkMode ? COND_LENS.dark : COND_LENS.light;
  const stationId = station ? station.id : null;

  // view = { id, data, pending } — id-tagged so a stale reading can never
  // render under a different home for even one frame. Seeded from the §3.5
  // session cache: toggling back to a loaded home paints instantly, no
  // skeleton flash. A cache MISS seeds pending:true so the very first
  // committed render is the skeleton — never a blank frame that then pops
  // to ~54px when the effect kicks in (reviewer: cold-mount CLS). The
  // !lib/!station early-return below keeps this from ever stranding a
  // perpetual skeleton.
  const [view, setView] = useState(() => {
    const cached = lib && stationId ? lib.peekCache(stationId) : null;
    return { id: stationId, data: cached, pending: !cached };
  });

  useEffect(() => {
    if (!lib || !stationId) return undefined;
    const cached = lib.peekCache(stationId);
    if (cached) {
      setView({ id: stationId, data: cached, pending: false });
      return undefined;
    }
    setView({ id: stationId, data: null, pending: true });
    let cancelled = false;
    // fetchConditions never rejects — resolves null on total failure (§3.3).
    // NOTE: `station` is read here under a [stationId] dep — station records
    // are static per id (stations.json), so id identity covers the coords.
    lib.fetchConditions(station).then((norm) => {
      if (!cancelled) setView({ id: stationId, data: norm, pending: false });
    });
    return () => { cancelled = true; };
  }, [stationId]);

  // Keyframes for the 200ms resolve fade (§1.2 MOTION) — injected once.
  // skeletonPulse already lives in flows.html; the global reduced-motion
  // block there floors both, and the reducedMotion prop gates them here too.
  useEffect(() => {
    const styleId = 'flows-conditions-keyframes';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes flowsCondFade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  if (!lib || !station) return null;

  // Stale-view guard: if the last settled view belongs to another home, fall
  // back to the new home's cache (or the skeleton) while the effect catches up.
  const current = view.id === stationId
    ? view
    : { id: stationId, data: lib.peekCache(stationId), pending: true };
  const { data, pending } = current;

  if (!data && !pending) return null; // no home reading ever → no strip (§3.3)

  const metrics = isMobile ? CELL_METRICS.mobile : CELL_METRICS.desktop;
  const cols = isMobile ? 3 : 6;

  const cellBase = {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 2, padding: '2px 8px', minWidth: 0, minHeight: metrics.minHeight,
  };
  // §1.2 DIVIDERS — index-mapped over the RENDERED cells: hairline between
  // columns, none at a row's outer edges / wrap seams.
  const dividerFor = (i) => (i % cols !== 0 ? `1px solid ${lens.hairline}` : 'none');

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: `repeat(${cols}, 1fr)`,
    rowGap: isMobile ? 10 : 0,
  };

  const valueLine = {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
    fontFamily: 'var(--font-display)', fontWeight: 800,
    fontSize: metrics.valuePx, lineHeight: 1, color: lens.ink,
    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
  };
  const metaLine = {
    fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: 9,
    letterSpacing: '0.1em', textTransform: 'uppercase', color: lens.muted,
    lineHeight: 1.1, textAlign: 'center', maxWidth: '100%',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  };
  // §2.1 — the ringed band dot. Fill carries hue ONLY; the muted ring is the
  // measured perceivable boundary (≥5.4:1 dark / ≥6.4:1 light, floor 3.0).
  const dotStyle = (band) => ({
    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
    background: lib.BAND[band][darkMode ? 'dark' : 'light'],
    border: `1px solid ${lens.muted}`,
  });

  let body;
  if (!data) {
    // §3.1 skeleton — 6 cells at EXACT final dimensions; height reserved from
    // first paint so the vertical axis never shifts (AC-P3-7).
    const barBg = darkMode ? 'rgba(233,231,226,0.10)' : 'rgba(25,21,18,0.08)';
    const pulse = reducedMotion ? 'none' : 'skeletonPulse 1.6s ease-in-out infinite';
    body = (
      <div style={gridStyle}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} style={{ ...cellBase, borderLeft: dividerFor(i) }}>
            <span style={{ width: 44, height: metrics.valuePx, borderRadius: 4, background: barBg, animation: pulse }} />
            <span style={{ width: 56, height: metrics.metaPx, borderRadius: 3, background: barBg, animation: pulse }} />
          </div>
        ))}
      </div>
    );
  } else {
    const stats = buildConditionStats(data, lib);
    if (stats.length === 0) return null; // shape arrived but nothing renderable
    body = (
      <div style={{ ...gridStyle, animation: reducedMotion ? 'none' : 'flowsCondFade 200ms ease' }}>
        {stats.map((stat, i) => (
          <div key={stat.key} style={{ ...cellBase, borderLeft: dividerFor(i) }}>
            <span style={COND_SR_ONLY}>{stat.sr}</span>
            <span aria-hidden="true" style={valueLine}>
              {typeof stat.band === 'number' && <span style={dotStyle(stat.band)} />}
              {stat.moon && <span style={{ fontSize: 9, color: lens.muted }}>☾</span>}
              <span>{stat.value}</span>
            </span>
            <span aria-hidden="true" style={metaLine}>{stat.meta}</span>
          </div>
        ))}
      </div>
    );
  }

  const stationName = window.displayStationName ? window.displayStationName(station) : station.name;

  return (
    // §1.1 placement — the 720 column under the HomeChip, above the notice
    // bar. Same slot geometry as renderNotice. Static text only: zero tab
    // stops, no aria-live (§4 — home-change is announced by §6.5 already).
    <section
      aria-label={COND_COPY.ARIA_NAME + stationName}
      style={{ maxWidth: 720, margin: '8px auto 0', padding: '0 20px', position: 'relative', zIndex: 5 }}
    >
      <div style={{
        ...window.glassStyle(darkMode, 'solid'),
        border: `1px solid ${lens.hairline}`,
        borderRadius: Math.min(radius, 14),
        padding: '10px 14px',
      }}>
        <div aria-hidden="true" style={{ ...metaLine, textAlign: 'left', marginBottom: 6 }}>
          {COND_COPY.KICKER}
        </div>
        {body}
        {/* R1 — Open-Meteo CC-BY attribution (LICENSE-REQUIRED): a visible link
            beside the displayed data. Lives INSIDE the shelf, after {body}, so
            it renders for BOTH the skeleton and resolved states → part of the
            reserved block, zero layout shift on resolve (and absent whenever the
            strip returns null — no strip, no attribution). Muted §0.5 token
            (≥4.5:1 both themes on the §0 composite). This adds ONE legitimate tab
            stop, in focus order between the home-chip and the feed (its visual
            slot); the strip's earlier "zero tab stops" note predates this legal
            requirement — a usable link must be focusable. */}
        <div style={{ marginTop: 8, textAlign: 'right', lineHeight: 1 }}>
          <a
            href={COND_COPY.ATTR_HREF}
            target="_blank"
            rel="noopener"
            aria-label={COND_COPY.ATTR_ARIA}
            className="focus-double-ring"
            style={{
              display: 'inline-block',
              fontFamily: 'var(--font-body)', fontSize: 9, fontWeight: 700,
              letterSpacing: '0.1em', textTransform: 'uppercase',
              color: lens.muted, textDecoration: 'none',
            }}
          >{COND_COPY.ATTR_TEXT}</a>
        </div>
      </div>
    </section>
  );
}

window.FlowsConditions = FlowsConditions;
